import {
  IComposerTransitionStep,
  shouldOpenTransitionComposer,
} from 'src/app/workflows/shouldOpenTransitionComposer';
import { useTransitionComposer } from 'src/app/workflows/useTransitionComposer';
import { NotificationCompositionRequest } from 'src/api/FiretrolApiClient';
import { getErrorMessage, logger, showErrorToast } from 'src/utils';

export interface IWorkflowTransitionStep extends IComposerTransitionStep {
  label?: string | undefined;
}

export interface IWorkflowTransitionValues {
  id?: string | undefined;
  stepIndex?: number | undefined;
  workflow?: { steps?: IWorkflowTransitionStep[] } | undefined;
  attachments?: unknown[] | undefined;
  filesToAdd: unknown[];
}

export interface IUseWorkflowFormTransitionsOptions<TDto> {
  values: IWorkflowTransitionValues;
  submitWorkflow: (
    status: string,
    stepIndex: number,
    saveOnly?: boolean,
    extra?: { notification?: NotificationCompositionRequest },
  ) => Promise<TDto | null>;
  transition: ReturnType<typeof useTransitionComposer>;
  /** Lower-case record noun used in the blocked-submit and failure toasts, e.g. "drawing". */
  entityLabel: string;
}

/**
 * @registry The submit path shared by the workflow record forms: blocks a forward move
 * that has no document attached, hands a transition into an external/PDF step to the
 * notification composer instead of writing it, and submits everything else directly.
 * `submitTransition` completes the deferred write once the composer sends or skips.
 */
export function useWorkflowFormTransitions<TDto>({
  values,
  submitWorkflow,
  transition,
  entityLabel,
}: IUseWorkflowFormTransitionsOptions<TDto>) {
  const onSubmitClick = async (status: string, nextStepIndex: number) => {
    const currentStepIndex = values.stepIndex ?? -1;
    const isForward = nextStepIndex === currentStepIndex + 1;
    const targetStep = values.workflow?.steps?.[nextStepIndex] ?? null;

    // A record cannot advance without a document, but Save Draft / Revise /
    // Reject (all non-forward) must stay available while one is still being
    // assembled.
    const hasDocument =
      values.filesToAdd.length > 0 || (values.attachments?.length ?? 0) > 0;
    if (isForward && !hasDocument) {
      showErrorToast(
        `Attach a document before submitting this ${entityLabel}.`,
      );
      return;
    }

    // Reject and Revise land at stepIndex -1 without the composer: the backend
    // stages no notification for a non-forward move and sends the rejection
    // email itself.
    if (
      shouldOpenTransitionComposer({
        isForward,
        hasId: !!values.id,
        targetStep,
      })
    ) {
      await transition.open(
        { status, stepIndex: nextStepIndex, stepLabel: targetStep.label ?? '' },
        `StepAdvanced:${nextStepIndex}`,
      );
      return;
    }

    await submitWorkflow(status, nextStepIndex);
  };

  const submitTransition = async (request: NotificationCompositionRequest) => {
    const pending = transition.pending;
    if (!pending) return;
    try {
      const result = await submitWorkflow(
        pending.status,
        pending.stepIndex,
        false,
        { notification: request },
      );
      if (!result) {
        showErrorToast(
          `Unable to submit the ${entityLabel}. Please try again.`,
        );
        return;
      }
      transition.close();
    } catch (err: unknown) {
      logger.error(`Failed to submit ${entityLabel} transition`, { err });
      showErrorToast(getErrorMessage(err));
    }
  };

  return { onSubmitClick, submitTransition };
}
